import { ChangeEvent, useCallback } from 'react';

import { Input, SelectV2, Text } from '@pukka/ui-components';
import { useSnapshot } from 'valtio';

import { FormInput } from './FormInput';
import { BusinessFormState, onChangeValue, ValidationState } from './GopayActivationForm.state';

interface BusinessDetailFormProps {
  readonly BusinessState?: typeof BusinessFormState;
  readonly Validation?: typeof ValidationState;
}

function BusinessDetailForm({ BusinessState = BusinessFormState, Validation = ValidationState }: BusinessDetailFormProps) {
  const {
    dirty,
    id,
    name,
    nameById,
    addressById,
    citizenship,
    directorPermitNumber,
    foundingCity,
    foundingDate,
    npwp,
    nameByNpwp,
    addressByNpwp,
    siup,
    stakeholderName,
  } = useSnapshot(BusinessState);
  const { businessValidation } = useSnapshot(Validation);

  const onChangeDate = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    onChangeValue('business', 'foundingDate')(e.target.value);
  }, []);

  return (
    <div>
      <Text as="h3" variant="title-2" weight="bold" className="mb-4" color="blue">
        Data Badan Usaha Merchant
      </Text>
      <FormInput
        required
        label="Nama Badan Usaha"
        error={dirty.has('name') && !businessValidation.name.success && businessValidation.name.error}
      >
        <Input type="text" value={name} onChangeValue={onChangeValue('business', 'name')} placeholder="Nama badan usaha" maxLength={100} />
      </FormInput>
      <FormInput
        required
        label="Kota Pendirian Usaha"
        description="Contoh: Jakarta Barat"
        error={dirty.has('foundingCity') && !businessValidation.foundingCity.success && businessValidation.foundingCity.error}
      >
        <Input
          type="text"
          value={foundingCity}
          onChangeValue={onChangeValue('business', 'foundingCity')}
          placeholder="Kota pendirian"
          maxLength={50}
        />
      </FormInput>
      <FormInput
        required
        label="Tanggal Pendirian Usaha"
        description="Isi sesuai dengan yang tertulis di Akta jika PT/ CV/ Yayasan."
        error={dirty.has('foundingDate') && !businessValidation.foundingDate.success && businessValidation.foundingDate.error}
      >
        <input
          type="date"
          id="foundingDate"
          name="foundingDate"
          data-testid="foundingDate"
          className="rounded-md border-neutral-300"
          value={foundingDate}
          onChange={onChangeDate}
        />
      </FormInput>
      <FormInput
        required
        label="Nomor Izin Usaha"
        description="Isi sesuai nomor SIUP/NIB."
        error={dirty.has('siup') && !businessValidation.siup.success && businessValidation.siup.error}
      >
        <Input type="tel" value={siup} onChangeValue={onChangeValue('business', 'siup')} placeholder="Nomor izin usaha" maxLength={30} />
      </FormInput>
      <FormInput
        required
        label="Nama Pemegang Saham"
        description="Isi sesuai dengan yang tercantum di Akta Pendirian."
        error={dirty.has('stakeholderName') && !businessValidation.stakeholderName.success && businessValidation.stakeholderName.error}
      >
        <Input
          type="text"
          value={stakeholderName}
          onChangeValue={onChangeValue('business', 'stakeholderName')}
          placeholder="Pemegang saham"
          maxLength={200}
        />
      </FormInput>
      <FormInput
        required
        label="Nama Direktur"
        description="Isi Nama Direktur  yang masi aktif sesuai Akta terupdate dan sesuai dengan yang tertulis di KTP/KITAS (jika WNA)."
        error={dirty.has('nameById') && !businessValidation.nameById.success && businessValidation.nameById.error}
      >
        <Input
          type="text"
          value={nameById}
          onChangeValue={onChangeValue('business', 'nameById')}
          placeholder="Nama direktur"
          maxLength={100}
        />
      </FormInput>
      <FormInput
        required
        label="Nomor KTP Direktur"
        description="Isi sesuai dengan yang tertulis di KTP."
        error={dirty.has('id') && !businessValidation.id.success && businessValidation.id.error}
      >
        <Input type="tel" value={id} onChangeValue={onChangeValue('business', 'id')} placeholder="Nomor KTP" maxLength={16} />
      </FormInput>
      <FormInput
        label="Permit Number Direktur"
        description="Khusus untuk WNA (Warga Negara Asing)."
        error={
          dirty.has('directorPermitNumber') &&
          !businessValidation.directorPermitNumber.success &&
          businessValidation.directorPermitNumber.error
        }
      >
        <Input
          type="text"
          value={directorPermitNumber}
          onChangeValue={onChangeValue('business', 'directorPermitNumber')}
          placeholder="Permit number"
          maxLength={30}
        />
      </FormInput>
      <FormInput
        required
        label="Alamat sesuai KTP/KITAS"
        error={dirty.has('addressById') && !businessValidation.addressById.success && businessValidation.addressById.error}
      >
        <Input
          type="text"
          value={addressById}
          onChangeValue={onChangeValue('business', 'addressById')}
          placeholder="Alamat lengkap"
          maxLength={250}
        />
      </FormInput>
      <FormInput
        required
        label="Kewarganegaraan"
        description="Isi sesuai dengan yang tertulis di KTP."
        error={dirty.has('citizenship') && !businessValidation.citizenship.success && businessValidation.citizenship.error}
      >
        <SelectV2
          placeholder="Kewarganegaraan"
          value={citizenship}
          onChange={onChangeValue('business', 'citizenship') as (value: string | number | boolean) => void}
        >
          <SelectV2.Option value="WNI">WNI (Warga Negara Indonesia)</SelectV2.Option>
          <SelectV2.Option value="WNA">WNA (Warga Negara Asing)</SelectV2.Option>
        </SelectV2>
      </FormInput>
      <FormInput
        required
        label="Nama di NPWP"
        description="Isi sesuai dengan yang tertulis di NPWP Badan Usaha."
        error={dirty.has('nameByNpwp') && !businessValidation.nameByNpwp.success && businessValidation.nameByNpwp.error}
      >
        <Input
          type="text"
          value={nameByNpwp}
          onChangeValue={onChangeValue('business', 'nameByNpwp')}
          placeholder="Nama lengkap"
          maxLength={100}
        />
      </FormInput>
      <FormInput
        required
        label="Nomor NPWP"
        description="Isi sesuai dengan yang tertulis di NPWP Badan Usaha."
        error={dirty.has('npwp') && !businessValidation.npwp.success && businessValidation.npwp.error}
      >
        <Input type="tel" value={npwp} onChangeValue={onChangeValue('business', 'npwp')} placeholder="Nomor NPWP" maxLength={20} />
      </FormInput>
      <FormInput
        required
        label="Alamat di NPWP"
        description="Isi sesuai dengan yang tertulis di NPWP Badan Usaha."
        error={dirty.has('addressByNpwp') && !businessValidation.addressByNpwp.success && businessValidation.addressByNpwp.error}
      >
        <Input
          type="text"
          value={addressByNpwp}
          onChangeValue={onChangeValue('business', 'addressByNpwp')}
          placeholder="Alamat sesuai NPWP"
          maxLength={250}
        />
      </FormInput>
    </div>
  );
}

export { BusinessDetailForm };
